
import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSecurityCompliance } from '@/hooks/useSecurityCompliance';
import { useSecurityFilters } from '@/hooks/useSecurityFilters';
import SecurityMetricsCards from './SecurityMetricsCards';
import SecurityEventsTab from './SecurityEventsTab';
import ViolationsTab from './ViolationsTab';
import AnalyticsTab from './AnalyticsTab';
import { Shield, Search, Filter } from 'lucide-react';

const SecurityAuditDashboard = () => {
  const {
    loading,
    securityEvents,
    violations,
    fetchSecurityEvents,
    fetchComplianceViolations,
    resolveSecurityEvent,
    updateViolationStatus,
  } = useSecurityCompliance();

  const { filters, updateFilter, setFilters } = useSecurityFilters();

  useEffect(() => {
    fetchSecurityEvents();
    fetchComplianceViolations();
  }, []);

  return (
    <div className="space-y-6">
      <SecurityMetricsCards 
        securityEvents={securityEvents} 
        violations={violations} 
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Audit Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="events" className="space-y-4">
            <TabsList>
              <TabsTrigger value="events">Security Events</TabsTrigger>
              <TabsTrigger value="violations">Compliance Violations</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>

            <div className="flex gap-4 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-black" />
                <Input
                  placeholder="Search events and violations..."
                  value={filters.searchTerm}
                  onChange={(e) => updateFilter('searchTerm', e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </div>

            <TabsContent value="events" className="mt-0">
              <SecurityEventsTab
                events={securityEvents}
                filters={filters}
                onUpdateFilter={updateFilter}
                onResolveEvent={resolveSecurityEvent}
                loading={loading}
              />
            </TabsContent>

            <TabsContent value="violations" className="mt-0">
              <ViolationsTab
                violations={violations}
                filters={filters}
                onUpdateFilter={updateFilter}
                onUpdateViolationStatus={updateViolationStatus}
                loading={loading}
              />
            </TabsContent>

            <TabsContent value="analytics" className="mt-0">
              <AnalyticsTab securityEvents={securityEvents} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityAuditDashboard;
